import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { useRef } from "react";
import { Toast } from "primereact/toast";
import { RawMaterialPurchaseListDTO } from "../../models/RawMaterials/RawMaterial-model";

interface DataTableProps {
  data: RawMaterialPurchaseListDTO[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (event: any) => void;
  onSortChange: (field: string, order: 1 | -1) => void;
  sortField?: string | undefined;
  sortOrder?: 1 | -1 | null;
}

const GrnReportsDatatable: React.FC<DataTableProps> = (props) => {
  const toast = useRef<Toast>(null);

  const onSort = (e: DataTableSortEvent) => {
    const field = e.sortField;
    const order = e.sortOrder as 1 | -1 | 0 | null | undefined;

    if (field && (order === 1 || order === -1)) {
      props.onSortChange(field, order);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  const rupee = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });

  const parseNumber = (val: any) => {
    if (val == null) return 0;
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      const n = parseFloat(val.replace(/[^0-9.-]/g, ""));
      return isNaN(n) ? 0 : n;
    }
    return 0;
  };

  const approxEqual = (a: number, b: number, eps = 1e-6) => Math.abs(a - b) < eps;

  const getQCStatus = (rowData: any) => {
    const invoiced = parseNumber(rowData.quantity ?? rowData.quantityWithUnit);
    const accepted = parseNumber(rowData.acceptedQuantity ?? rowData.acceptedQuantityWithUnit);
    const rejected = parseNumber(rowData.rejectedQuantity ?? rowData.rejectedQuantityWithUnit);

    const remainingFromApi = parseNumber(
      rowData.remainingQuantity ?? rowData.remainingQuantityWithUnit
    );
    const sumAR = accepted + rejected;
    const computedRemaining = Math.max(0, invoiced - sumAR);
    const remaining = remainingFromApi > 0 ? remainingFromApi : computedRemaining;

    let statusName = "QC Pending";

    if (approxEqual(invoiced, accepted) && invoiced > 0) {
      statusName = "Accepted";
    } else if (approxEqual(invoiced, rejected) && invoiced > 0) {
      statusName = "Rejected";
    } else if (approxEqual(invoiced, sumAR) && invoiced > 0) {
      statusName = "Accepted & Rejected";
    } else if (approxEqual(invoiced, remaining) && invoiced > 0) {
      statusName = "QC Pending";
    } else if (approxEqual(accepted, 0) && rejected > 0 && !approxEqual(rejected, invoiced)) {
      statusName = "Partially Rejected";
    } else {
      statusName = "Partially Accepted";
    }

    return statusName;
  };

  const invoiceNumberBody = (rowData: any) => {
    return rowData.invoiceNumber || rowData.invoiceId || "-";
  };

  const qcByBodyTemplate = (rowData: any) => {
    const status = getQCStatus(rowData);
    
    if (status === "QC Pending") {
      return "-";
    }
    
    return rowData.updatedByName || rowData.qcByName || "-";
  };

  const qcDateBodyTemplate = (rowData: any) => {
    const status = getQCStatus(rowData);
    
    if (status === "QC Pending") {
      return "-";
    }
    
    const dateToFormat = rowData.updatedDate || rowData.qcDate;
    return dateToFormat ? formatDate(dateToFormat) : "-";
  };

  return (
    <div className="col-12">
      <style>{`
        .grn-datatable ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .grn-datatable ::-webkit-scrollbar-track {
          background: transparent;
        }
        .grn-datatable ::-webkit-scrollbar-thumb {
          background: #ddd;
          border-radius: 4px;
        }
        .grn-datatable .p-datatable .p-datatable-header {
          background: white !important;
          border: none !important;
          padding: 0 !important;
        }
        .grn-datatable .p-datatable .p-datatable-thead > tr > th {
          background: linear-gradient(to bottom, #f9fafb, #f3f4f6) !important;
          border-bottom: 2px solid #e5e7eb !important;
          border-right: 1px solid #e5e7eb !important;
          padding: 1rem 0.75rem !important;
        }
        .grn-datatable .p-datatable .p-datatable-thead > tr > th:last-child {
          border-right: none !important;
        }
        .grn-datatable .p-datatable .p-datatable-tbody > tr > td {
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #f3f4f6 !important;
        }
        .grn-datatable .p-datatable .p-datatable-tbody > tr > td:last-child {
          border-right: none !important;
        }
        .grn-datatable .p-datatable .p-datatable-footer {
          border-top: 2px solid #e5e7eb !important;
          background: white !important;
          padding: 0.75rem !important;
        }
        .grn-datatable .p-datatable {
          border: 1px solid #e5e7eb !important;
          box-shadow: none !important;
          border-radius: 12px !important;
        }
        .grn-datatable .p-paginator {
          border: none !important;
          background: transparent !important;
        }
      `}</style>
      <Toast ref={toast} />
      <div style={{ width: "100%", padding: "0", margin: "0", overflow: "hidden" }}>
        {/* <DataTable
          value={props.data}
          paginator
          rows={props.pageSize}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          totalRecords={props.totalRecords}
          lazy
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          first={(props.pageNumber - 1) * props.pageSize}
          onPage={props.onPageChange}
          sortMode="single"
          sortField={props.sortField}
          sortOrder={props.sortOrder}
          onSort={onSort}
        > */}
<DataTable
  value={props.data}
  paginator
  rows={props.pageSize}
  first={(props.pageNumber - 1) * props.pageSize}
  totalRecords={props.totalRecords}
  rowsPerPageOptions={[5, 10, 25, 50, 100]}
  paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
  currentPageReportTemplate="{first} to {last} of {totalRecords}"
  lazy
  onPage={props.onPageChange}
  sortMode="single"
  sortField={props.sortField}
  sortOrder={props.sortOrder}
  onSort={onSort}
  stripedRows
  rowHover
  className="grn-datatable"
  style={{
    width: "100%",
    borderRadius: "12px",
    overflow: "hidden",
    background: "white",
    border: "1px solid #e5e7eb"
  }}
>            <Column
            field="rawMaterialPurchaseId"
            header="GRN no"
            style={{ width: "12%" }}
            sortable
          />

          <Column
            field="purchaseDate"
            header="GRN report Date"
            body={(rowData: any) => formatDate(rowData.purchaseDate)}
            style={{ width: "12%"   , whiteSpace: "nowrap" }}
            sortable
          />

          {/* NEW: Raw Material Purchase Id */}

          {/* existing columns (same order as your QC table, minus Actions) */}
          <Column
            field="rawMaterialName"
            header="Raw Material"
            style={{ width: "10%" }}
            sortable
          />
          <Column
            field="categoryName"
            header="Category"
            style={{ width: "15%" }}
            sortable
          />
          <Column
            field="invoiceNumber"
            header="Invoice No."
            body={invoiceNumberBody}
            style={{ width: "12%" }}
            sortable
          />
          <Column
            field="vendorName"
            header="Vendor"
            style={{ width: "15%" }}
            sortable
          />
          <Column
            field="quantity"
            header="Invoiced Qty"
            body={(rowData: any) => rowData.quantityWithUnit || "-"}
            style={{ width: "10%" }}
            sortable
          />
          <Column
            field="posAlias"
            header="Unit"
            style={{ width: "12%" }}
            sortable
          />
          <Column
            field="acceptedQuantity"
            header="Accepted Qty"
            body={(rowData: any) => rowData.acceptedQuantityWithUnit || "-"}
            style={{ width: "10%" }}
            sortable
          />
          <Column
            field="remainingQuantity"
            header="Remaining Qty"
            body={(rowData: any) => rowData.remainingQuantityWithUnit || "-"}
            style={{ width: "10%" }}
            sortable
          />
          <Column
            field="rejectedQuantity"
            header="Rejected Qty"
            body={(rowData: any) => rowData.rejectedQuantityWithUnit || "-"}
            style={{ width: "10%" }}
            sortable
          />
          <Column
            field="basicAmount"
            header="Base Price"
            style={{ width: "10%" }}
            body={(rowData: any) => rupee.format(parseNumber(rowData.basicAmount))}
            sortable
          />
          <Column
            field="taxAmount"
            header="Tax Amount"
            style={{ width: "10%" }}
            body={(rowData: any) => rupee.format(parseNumber(rowData.taxAmount))}
            sortable
          />
          <Column
            field="totalAmount"
            header="Total Amount"
            style={{ width: "10%" }}
            body={(rowData: any) => rupee.format(parseNumber(rowData.totalAmount))}
            sortable
          />
          <Column
            field="updatedByName"
            header="QC By"
            style={{ width: "10%", whiteSpace: "nowrap"}}
            body={qcByBodyTemplate}
            sortable
          />
          <Column
            field="updatedDate"
            header="QC Date"
            body={qcDateBodyTemplate}
            style={{ width: "10%" , whiteSpace: "nowrap" }}
            sortable
          />
        </DataTable>
      </div>
    </div>
  );
};

export default GrnReportsDatatable;
